/**
 * Pause d'abonnement (« mettre en pause plutôt que résilier »).
 *
 * Écrite UNIQUEMENT par le serveur stripe-b2b dans `clients/{id}.pause` (pose,
 * reprise, webhook Stripe). Ne JAMAIS passer par `disabled` pour la pause : il
 * ouvre HomeUnsubscribedModal, dont « Réactiver » crée un 2e abonnement →
 * double prélèvement.
 *
 * Dates en millisecondes (converties depuis les Timestamp Firestore).
 */
export type ClientPauseStatus = 'paused' | 'resumed' | 'canceled' | 'ended';

export type ClientPause = {
    status: ClientPauseStatus;
    months?: number | null;
    requestedAt?: number | null;
    /** Fin de la période payée : l'accès est coupé à partir d'ici. */
    startsAt: number | null;
    /** Levée de la pause par Stripe (3 jours avant la reprise du prélèvement) : l'accès revient. null = pause sans fin. */
    resumesAt: number | null;
    /** Échéance de reprise, prélevée normalement. */
    endsAt?: number | null;
    subscriptionId?: string;
    source?: string;
};

/** Accès coupé à l'instant `now` : pause en cours et now ∈ [startsAt, resumesAt). */
export function isPauseActive(pause: ClientPause | undefined | null, now: number = Date.now()): boolean {
    if (!pause || pause.status !== 'paused' || !pause.startsAt) return false;
    if (now < pause.startsAt) return false;
    return pause.resumesAt == null || now < pause.resumesAt;
}

/**
 * Prochain instant où `isPauseActive` change de valeur (début ou fin de pause),
 * pour re-rendre l'écran pile à ce moment (une TV peut rester des heures sur le
 * menu sans nouvel instantané Firestore). null si plus rien ne change.
 */
export function nextPauseBoundary(pause: ClientPause | undefined | null, now: number = Date.now()): number | null {
    if (!pause || pause.status !== 'paused') return null;
    const candidates = [pause.startsAt, pause.resumesAt].filter((t): t is number => typeof t === 'number' && t > now);
    return candidates.length ? Math.min(...candidates) : null;
}

type FirestoreDateLike = { toMillis?: () => number; seconds?: number } | Date | number | null | undefined;

const toMillis = (value: FirestoreDateLike): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') return value < 1e11 ? value * 1000 : value;
    if (value instanceof Date) return value.getTime();
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    return null;
};

/** `clients/{id}.pause` brut (Firestore) → ClientPause. Absent / illisible → undefined. */
export function pauseFromFirestore(raw: any): ClientPause | undefined {
    if (!raw || typeof raw !== 'object' || typeof raw.status !== 'string') return undefined;
    return {
        status: raw.status,
        months: raw.months ?? null,
        requestedAt: toMillis(raw.requestedAt),
        startsAt: toMillis(raw.startsAt),
        resumesAt: toMillis(raw.resumesAt),
        endsAt: toMillis(raw.endsAt),
        subscriptionId: raw.subscriptionId,
        source: raw.source,
    };
}
